// Comprehensive Admin View for CNC PM PWA per ORIGINAL_REQUEST § R2 & PROJECT.md
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  PinPad,
  MachineManager,
  TemplateEditor,
  ExcelImporter,
  InspectorManager,
  InspectionHistory,
  hashPinSha256,
  verifyPin,
  DEFAULT_PIN_HASH,
  PIN_SETTING_KEY,
} from '../components/admin';
import { Button, Card } from '../components/common';
import { getSetting, saveSetting } from '../db';
import {
  ShieldAlert,
  Cpu,
  FileText,
  FileSpreadsheet,
  Lock,
  Unlock,
  KeyRound,
  CheckCircle2,
  AlertCircle,
  Clock,
  Users,
  ClipboardList,
} from 'lucide-react';

export type AdminSubTab = 'machines' | 'templates' | 'importer' | 'inspectors' | 'history' | 'security';

export interface AdminViewProps {
  initialSubTab?: AdminSubTab;
}

// Session timeout duration: 5 minutes (300,000 ms)
export const SESSION_TIMEOUT_MS = 5 * 60 * 1000;

export const AdminView: React.FC<AdminViewProps> = ({ initialSubTab = 'machines' }) => {
  const [isUnlocked, setIsUnlocked] = useState<boolean>(false);
  const [activeSubTab, setActiveSubTab] = useState<AdminSubTab>(initialSubTab);

  // PIN Lock screen state
  const [enteredPin, setEnteredPin] = useState<string>('');
  const [pinError, setPinError] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [storedHash, setStoredHash] = useState<string>(DEFAULT_PIN_HASH);

  // Change PIN Form state
  const [currentPinInput, setCurrentPinInput] = useState<string>('');
  const [newPinInput, setNewPinInput] = useState<string>('');
  const [confirmPinInput, setConfirmPinInput] = useState<string>('');
  const [pinChangeMsg, setPinChangeMsg] = useState<string>('');
  const [pinChangeErr, setPinChangeErr] = useState<string>('');

  const lastActivityRef = useRef<number>(Date.now());
  const timerRef = useRef<any>(null);

  // Load stored PIN hash
  useEffect(() => {
    getSetting<string>(PIN_SETTING_KEY).then((hash) => {
      if (hash) setStoredHash(hash);
      else setStoredHash(DEFAULT_PIN_HASH);
    });
  }, []);

  // Inactivity / Session Lock Timer
  const lockSession = useCallback(() => {
    setIsUnlocked(false);
    setEnteredPin('');
    setPinError('');
  }, []);

  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!isUnlocked) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    lastActivityRef.current = Date.now();

    // Check every 10 seconds for session timeout
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= SESSION_TIMEOUT_MS) {
        lockSession();
      }
    }, 10000);

    const handleUserActivity = () => {
      resetInactivityTimer();
    };

    window.addEventListener('pointerdown', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('pointerdown', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
    };
  }, [isUnlocked, lockSession, resetInactivityTimer]);

  // Handle PIN Unlock
  const handlePinComplete = async (pin: string) => {
    setIsVerifying(true);
    setPinError('');
    try {
      const currentHash = (await getSetting<string>(PIN_SETTING_KEY)) || storedHash || DEFAULT_PIN_HASH;
      const match = await verifyPin(pin, currentHash);
      if (match) {
        setIsUnlocked(true);
        setEnteredPin('');
        setPinError('');
      } else {
        setPinError('รหัสผ่านไม่ถูกต้อง (Default PIN: 1234)');
        setEnteredPin('');
      }
    } catch (e) {
      console.error('[AdminView] PIN verify error:', e);
      setPinError('เกิดข้อผิดพลาดในการตรวจสอบรหัสผ่าน');
      setEnteredPin('');
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle Change PIN
  const handleChangePin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinChangeMsg('');
    setPinChangeErr('');

    if (!/^\d{4}$/.test(currentPinInput)) {
      setPinChangeErr('รหัสผ่านปัจจุบันต้องเป็นตัวเลข 4 หลัก');
      return;
    }
    if (!/^\d{4}$/.test(newPinInput)) {
      setPinChangeErr('รหัสผ่านใหม่ต้องเป็นตัวเลข 4 หลัก');
      return;
    }
    if (newPinInput !== confirmPinInput) {
      setPinChangeErr('รหัสผ่านใหม่และการยืนยันไม่ตรงกัน');
      return;
    }

    const currentMatches = await verifyPin(currentPinInput, storedHash);
    if (!currentMatches) {
      setPinChangeErr('รหัสผ่านเดิมไม่ถูกต้อง');
      return;
    }

    try {
      const newHash = await hashPinSha256(newPinInput);
      await saveSetting(PIN_SETTING_KEY, newHash);
      setStoredHash(newHash);
      setPinChangeMsg('เปลี่ยนรหัสผ่าน 4 หลักสำเร็จเรียบร้อย');
      setCurrentPinInput('');
      setNewPinInput('');
      setConfirmPinInput('');
      setTimeout(() => setPinChangeMsg(''), 4000);
    } catch (err: any) {
      setPinChangeErr(`เปลี่ยนรหัสผ่านไม่สำเร็จ: ${err.message || 'Unknown error'}`);
    }
  };

  // ----------------------------------------------------
  // Render: Locked State (PIN Pad Gate)
  // ----------------------------------------------------
  if (!isUnlocked) {
    return (
      <div className="max-w-md mx-auto py-6 animate-in fade-in duration-200">
        <Card
          title={
            <div className="flex items-center gap-2 text-amber-700 font-bold">
              <Lock className="w-5 h-5" />
              <span>เข้าสู่ระบบแผงควบคุมผู้ดูแล (Admin Access)</span>
            </div>
          }
          subtitle="ระบบนี้ได้รับการป้องกันด้วยรหัสผ่าน 4 หลัก เพื่อความปลอดภัยในการแก้ไขเทมเพลตและเครื่องจักร"
        >
          <div className="space-y-6 py-2">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 mx-auto shadow-xs">
              <KeyRound className="w-8 h-8" />
            </div>

            <p className="text-xs text-slate-500 text-center">
              กรุณากดรหัสผ่าน 4 หลักบนแป้นพิมพ์สัมผัสเพื่อปลดล็อก
            </p>

            <PinPad
              pin={enteredPin}
              onPinChange={(v) => {
                setEnteredPin(v);
                if (pinError) setPinError('');
              }}
              onComplete={handlePinComplete}
              disabled={isVerifying}
              error={pinError}
            />

            <div className="text-center pt-2 border-t border-slate-100">
              <p className="text-[11px] text-slate-500 font-mono">
                ค่าเริ่มต้นจากโรงงาน (Default PIN): <strong className="text-slate-700 font-bold">1234</strong>
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ----------------------------------------------------
  // Render: Unlocked State (Admin Panel Tabs)
  // ----------------------------------------------------
  return (
    <div className="space-y-6 animate-in fade-in duration-150">
      {/* Admin Panel Header & Status */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <Unlock className="w-3.5 h-3.5" />
              Admin Session Active
            </span>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> ล็อกอัตโนมัติเมื่อไม่มีการใช้งาน 5 นาที
            </span>
          </div>
          <h1 className="text-xl font-bold text-slate-900 mt-1">
            แผงควบคุมการจัดการเครื่องจักรและแบบฟอร์มตรวจสอบ
          </h1>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={lockSession}
          leftIcon={<Lock className="w-4 h-4 text-amber-600" />}
        >
          ล็อกแผงควบคุม (Lock Now)
        </Button>
      </div>

      {/* Sub Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-slate-200 overflow-x-auto pb-1">
        <button
          type="button"
          onClick={() => setActiveSubTab('machines')}
          className={`min-h-[48px] px-5 py-2.5 rounded-t-xl font-bold text-sm transition-all flex items-center gap-2 border-b-2 whitespace-nowrap cursor-pointer ${
            activeSubTab === 'machines'
              ? 'bg-white text-blue-600 border-blue-600 shadow-2xs'
              : 'text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-100'
          }`}
        >
          <Cpu className="w-4 h-4" />
          <span>จัดการเครื่องจักร (Machines)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('templates')}
          className={`min-h-[48px] px-5 py-2.5 rounded-t-xl font-bold text-sm transition-all flex items-center gap-2 border-b-2 whitespace-nowrap cursor-pointer ${
            activeSubTab === 'templates'
              ? 'bg-white text-emerald-600 border-emerald-600 shadow-2xs'
              : 'text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-100'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>แก้ไขเทมเพลต (Template Editor)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('importer')}
          className={`min-h-[48px] px-5 py-2.5 rounded-t-xl font-bold text-sm transition-all flex items-center gap-2 border-b-2 whitespace-nowrap cursor-pointer ${
            activeSubTab === 'importer'
              ? 'bg-white text-amber-600 border-amber-600 shadow-2xs'
              : 'text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-100'
          }`}
        >
          <FileSpreadsheet className="w-4 h-4" />
          <span>นำเข้า Excel (.xlsx Importer)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('inspectors')}
          className={`min-h-[48px] px-5 py-2.5 rounded-t-xl font-bold text-sm transition-all flex items-center gap-2 border-b-2 whitespace-nowrap cursor-pointer ${
            activeSubTab === 'inspectors'
              ? 'bg-white text-blue-600 border-blue-600 shadow-2xs'
              : 'text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-100'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>ผู้ตรวจสอบ (Inspectors)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('history')}
          className={`min-h-[48px] px-5 py-2.5 rounded-t-xl font-bold text-sm transition-all flex items-center gap-2 border-b-2 whitespace-nowrap cursor-pointer ${
            activeSubTab === 'history'
              ? 'bg-white text-blue-600 border-blue-600 shadow-2xs'
              : 'text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-100'
          }`}
        >
          <ClipboardList className="w-4 h-4" />
          <span>ประวัติการตรวจ (History)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('security')}
          className={`min-h-[48px] px-5 py-2.5 rounded-t-xl font-bold text-sm transition-all flex items-center gap-2 border-b-2 whitespace-nowrap cursor-pointer ${
            activeSubTab === 'security'
              ? 'bg-white text-purple-600 border-purple-600 shadow-2xs'
              : 'text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-100'
          }`}
        >
          <KeyRound className="w-4 h-4" />
          <span>ความปลอดภัย (PIN Security)</span>
        </button>
      </div>

      {/* Sub-Tab 1: Machine Management */}
      {activeSubTab === 'machines' && <MachineManager />}

      {/* Sub-Tab 2: Template Editor */}
      {activeSubTab === 'templates' && <TemplateEditor />}

      {/* Sub-Tab 3: Excel Importer */}
      {activeSubTab === 'importer' && (
        <ExcelImporter
          onImportSuccess={(tplId) => {
            setActiveSubTab('templates');
          }}
        />
      )}

      {/* Sub-Tab 4: Inspector Management */}
      {activeSubTab === 'inspectors' && <InspectorManager />}

      {/* Sub-Tab 5: Inspection History & Photos */}
      {activeSubTab === 'history' && <InspectionHistory />}

      {/* Sub-Tab 6: Security & PIN Settings */}
      {activeSubTab === 'security' && (
        <div className="max-w-xl mx-auto space-y-6">
          <Card
            title={
              <div className="flex items-center gap-2 text-purple-700 font-bold">
                <KeyRound className="w-5 h-5 text-purple-600" />
                <span>เปลี่ยนรหัสผ่านแผงควบคุม (Change Admin PIN)</span>
              </div>
            }
            subtitle="รหัสผ่านจะถูกเข้ารหัสแบบ SHA-256 Web Crypto hashing ก่อนบันทึกลงใน IndexedDB เพื่อความปลอดภัย"
          >
            <form onSubmit={handleChangePin} className="space-y-4">
              {pinChangeMsg && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-xl flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                  <span>{pinChangeMsg}</span>
                </div>
              )}
              {pinChangeErr && (
                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                  <span>{pinChangeErr}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  รหัสผ่านปัจจุบัน (Current 4-digit PIN) *
                </label>
                <input
                  type="password"
                  maxLength={4}
                  value={currentPinInput}
                  onChange={(e) => setCurrentPinInput(e.target.value)}
                  placeholder="••••"
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-center text-xl font-mono tracking-widest text-slate-900 min-h-[48px] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    รหัสผ่านใหม่ (New 4-digit PIN) *
                  </label>
                  <input
                    type="password"
                    maxLength={4}
                    value={newPinInput}
                    onChange={(e) => setNewPinInput(e.target.value)}
                    placeholder="••••"
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-center text-xl font-mono tracking-widest text-slate-900 min-h-[48px] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    ยืนยันรหัสผ่านใหม่ (Confirm PIN) *
                  </label>
                  <input
                    type="password"
                    maxLength={4}
                    value={confirmPinInput}
                    onChange={(e) => setConfirmPinInput(e.target.value)}
                    placeholder="••••"
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-center text-xl font-mono tracking-widest text-slate-900 min-h-[48px] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div className="pt-2 flex items-center justify-end">
                <Button variant="primary" type="submit" leftIcon={<KeyRound className="w-4 h-4" />}>
                  บันทึกรหัสผ่านใหม่
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};
